import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "./shared";
import { NEW_LEDGER_OPTION, fixedDepositBlank } from "./constants";
import { ACCOUNTING_BASE } from "./accountingNav";
import FixedDepositForm from "./FixedDepositForm";

export default function FixedDepositEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fd, setFd] = useState(null);
  const [assetLedgers, setAssetLedgers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [form, setForm] = useState(fixedDepositBlank);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [fdRes, ledgersRes, bankRes] = await Promise.all([
          api.get(`/society/accounting/engine/fixed-deposits/${id}`),
          api.get("/society/accounting/engine/ledgers").catch(() => ({ data: [] })),
          api.get("/society/accounting/engine/bank-accounts").catch(() => ({ data: [] })),
        ]);
        const row = fdRes.data;
        setFd(row);
        setAssetLedgers((Array.isArray(ledgersRes.data) ? ledgersRes.data : []).filter((l) => l.ledgerGroup === "asset"));
        setBankAccounts(Array.isArray(bankRes.data) ? bankRes.data : []);
        setForm({
          bankAccountId: row.bankAccountId || "", bankName: row.bankName || "", fdNumber: row.fdNumber || "",
          instrumentName: row.instrumentName || "", principal: row.principal ?? "", startDate: row.startDate || "",
          maturityDate: row.maturityDate || "", interestRate: row.interestRate ?? "", maturityValue: row.maturityValue ?? "",
          linkedLedgerId: row.linkedLedgerId || NEW_LEDGER_OPTION, newLedgerName: "", notes: row.notes || "",
        });
      } catch (err) {
        toast.error(formatErr(err, "Could not load this fixed deposit"));
        navigate(`${ACCOUNTING_BASE}/fixed-deposits`, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const save = async () => {
    if (!form.fdNumber.trim()) { toast.error("FD number is required"); return; }
    try {
      setBusy(true);
      let linkedLedgerId = form.linkedLedgerId;
      if (linkedLedgerId === NEW_LEDGER_OPTION) {
        const ledgerName = (form.newLedgerName || `FD ${form.fdNumber}`).trim();
        const ledgerRes = await api.post("/society/accounting/engine/ledgers", { name: ledgerName, ledgerGroup: "asset", openingBalance: Number(form.principal) || 0, openingSide: "debit" });
        linkedLedgerId = ledgerRes.data.id;
      }
      await api.patch(`/society/accounting/engine/fixed-deposits/${id}`, {
        ...form, linkedLedgerId, principal: Number(form.principal || 0), interestRate: Number(form.interestRate || 0), maturityValue: Number(form.maturityValue || 0),
      });
      toast.success("Fixed deposit updated");
      navigate(`${ACCOUNTING_BASE}/fixed-deposits/${id}`);
    } catch (err) {
      toast.error(formatErr(err, "Could not save fixed deposit"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B7280]">Loading...</div>;
  if (!fd) return null;

  const fieldsLocked = fd.status !== "ACTIVE";

  return (
    <div className="space-y-5">
      <div>
        <Link to={`${ACCOUNTING_BASE}/fixed-deposits/${id}`} className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6B7280] hover:text-[#111827]">
          &larr; Back to fixed deposit
        </Link>
        <h2 className="mt-1 text-lg font-extrabold text-[#111827]">Edit {fd.bankName} - {fd.fdNumber}</h2>
      </div>

      <SectionCard title="Fixed deposit details" subtitle={fieldsLocked ? "This FD is matured/closed - only identification and notes can still be edited." : undefined}>
        <FixedDepositForm
          form={form}
          setForm={setForm}
          bankAccounts={bankAccounts}
          availableLedgers={assetLedgers}
          fieldsLocked={fieldsLocked}
          ledgerLocked={!!fd.hasPostedTransactions}
          busy={busy}
          onSubmit={save}
          onCancel={() => navigate(`${ACCOUNTING_BASE}/fixed-deposits/${id}`)}
          submitLabel="Save changes"
          isEdit
        />
      </SectionCard>
    </div>
  );
}
